import { useState, useCallback, useMemo } from "react";
import useStrictInputSanitizer from "./useStrictInputSanitizer";
import SanitizationWarningDialog from "../dialogs/SanitizationWarningDialog";

export default function useSanitizationGuard() {
    const sanitize = useStrictInputSanitizer();

    // ---------- Dialog state ----------
    const initial = useMemo(
        () => ({
            open: false,
            chars: [],
            terms: [],     // forbidden words/phrases (for clarity)
            patterns: [],  // kept internal/hidden
            field: "",
            value: ""
        }),
        []
    );
    const [state, setState] = useState(initial);
    const close = useCallback(() => setState(initial), [initial]);

    // ---------- Pattern → character hints (aligned with minimal-risk set) ----------
    const PATTERN_CHAR_HINTS = useMemo(
        () => ({
            SCRIPT_TAG: ["<", ">"],
            ON_EVENT_HANDLER: ["=", "'", '"'],
            JS_URL: [":"],
            DATA_URL: [":"],

            SQL_UNION_SELECT: [], // show as terms
            SQL_DROP_TABLE: [],
            SQL_DELETE_FROM: [],
            SQL_COMMENT_BLOCK: ["/", "*"],
            SQL_TAUTOLOGY: ["=", "+"],

            NOSQL_OPERATOR: ["$"],

            TPL_JS_TEMPLATE: ["$", "{", "}"],
            TPL_MUSTACHE: ["{", "}"],

            PATH_TRAVERSAL: [".", "/", "\\"],

            // Bare ";" is intentionally not flagged — see
            // useStrictInputSanitizer.js for why.
            SHELL_CHAIN: ["||", "&&"],

            URL_ENCODED: ["%"],
            HTML_ENTITY: ["&", "#", ";"],

            // XML_DTD removed – no more <!DOCTYPE / <!ENTITY> hints
        }),
        []
    );

    // Only show safe, expected characters in the dialog
    const DISPLAY_WHITELIST = useMemo(
        () =>
            new Set([
                "<", ">", "{", "}", "(", ")", "[", "]", "`", "'", '"', ";", ":", "\\", "/",
                "|", "&", "$", "%", "*", "+", "=", "^", "~", ",", "!", "?", "#", "@", ".",
                "-", "_", ":", "||", "&&"
            ]),
        []
    );

    const expandCharsWithPatternHints = useCallback(
        (foundChars, patternIds) => {
            const out = new Set(foundChars);
            for (const id of patternIds || []) {
                const hints = PATTERN_CHAR_HINTS[id] || [];
                for (const ch of hints) {
                    if (DISPLAY_WHITELIST.has(ch)) out.add(ch);
                }
            }
            return Array.from(out);
        },
        [PATTERN_CHAR_HINTS, DISPLAY_WHITELIST]
    );

    // Extract concrete forbidden TERMS (so dialog is never vague)
    const extractForbiddenTerms = useCallback((input, patternIds) => {
        const terms = new Set();
        if (!patternIds || !patternIds.length) return [];

        // SQL combos
        if (patternIds.includes("SQL_UNION_SELECT")) {
            const m = input.match(/\bUNION\b\s+SELECT\b/gi);
            if (m) m.forEach(() => terms.add("UNION SELECT"));
        }
        if (patternIds.includes("SQL_DROP_TABLE")) {
            const m = input.match(/\bDROP\b\s+(TABLE|DATABASE)\b/gi);
            if (m) m.forEach(k => terms.add(k.toUpperCase().includes("TABLE") ? "DROP TABLE" : "DROP DATABASE"));
        }
        if (patternIds.includes("SQL_DELETE_FROM")) {
            const m = input.match(/\bDELETE\b\s+FROM\b/gi);
            if (m) m.forEach(() => terms.add("DELETE FROM"));
        }
        if (patternIds.includes("SQL_COMMENT_BLOCK")) {
            if (/\/\*/.test(input)) terms.add("/*");
            if (/\*\//.test(input)) terms.add("*/");
        }
        if (patternIds.includes("SQL_TAUTOLOGY")) {
            if (/\bOR\b\s+\d+\s*=\s*\d+/i.test(input)) terms.add("OR 1=1");
        }

        // NoSQL
        if (patternIds.includes("NOSQL_OPERATOR")) {
            const m = input.match(/\$(ne|nin|where|gt|lt|gte|lte|in|regex)\b/gi);
            if (m) m.forEach(t => terms.add(`$${t.split("$").pop().toLowerCase()}`));
        }

        // XSS/HTML
        if (patternIds.includes("SCRIPT_TAG")) terms.add("<script>…</script>");
        if (patternIds.includes("ON_EVENT_HANDLER")) {
            const m = input.match(/\bon\w+\s*=/gi);
            if (m) m.forEach(t => terms.add(t.replace(/\s*=/, "=")));
        }
        if (patternIds.includes("JS_URL") && /javascript:\s*/i.test(input)) terms.add("javascript:");
        if (patternIds.includes("DATA_URL") && /data:\s*/i.test(input)) terms.add("data:");

        // Templates
        if (patternIds.includes("TPL_JS_TEMPLATE")) terms.add("${…}");
        if (patternIds.includes("TPL_MUSTACHE")) terms.add("{{…}}");

        // Path / Shell
        if (patternIds.includes("PATH_TRAVERSAL")) {
            if (/\.\.\//.test(input)) terms.add("../");
            if (/\.{2}\\/.test(input)) terms.add("..\\");
        }
        if (patternIds.includes("SHELL_CHAIN")) {
            if (/\|\|/.test(input)) terms.add("||");
            if (/&&/.test(input)) terms.add("&&");
        }

        // Encodings
        if (patternIds.includes("URL_ENCODED") && /%[0-9A-Fa-f]{2}/.test(input)) {
            terms.add("URL-encoded sequences (%xx)");
        }
        if (patternIds.includes("HTML_ENTITY") && /&#x?[0-9A-Fa-f]+;?/.test(input)) {
            terms.add("HTML entities (e.g., &#x3C;)");
        }

        return Array.from(terms);
    }, []);

    // ---------- Strict check ----------
    const check = useCallback(
        (value, fieldLabel = "Field") => {
            const input = typeof value === "string" ? value : String(value ?? "");
            const res = sanitize(input);

            if (!res.hasBlacklisted) return { blocked: false, details: res };

            const expandedChars = expandCharsWithPatternHints(res.foundChars, res.foundPatterns);
            const terms = extractForbiddenTerms(input, res.foundPatterns);

            setState({
                open: true,
                chars: expandedChars,
                terms,
                patterns: [],
                field: fieldLabel,
                value: input
            });
            return { blocked: true, details: res };
        },
        [sanitize, expandCharsWithPatternHints, extractForbiddenTerms]
    );

    // ---------- Free-text check (use for sentences/teacher text) ----------
    const CRITICAL_CHARS = useMemo(
        () => new Set(["{", "}", "`", "$", "|", "\\", "/", "%", "^", "&"]), // matches sanitizer blacklist
        []
    );

    const checkFreeText = useCallback(
        (value, fieldLabel = "Field") => {
            const input = typeof value === "string" ? value : String(value ?? "");
            const res = sanitize(input);

            if (!res.hasBlacklisted) return { blocked: false, details: res };

            // keep only truly critical chars (allow commas, periods, quotes, etc.)
            const filteredChars = (res.foundChars || []).filter(ch => CRITICAL_CHARS.has(ch));
            const expandedChars = expandCharsWithPatternHints(filteredChars, res.foundPatterns);

            // keep terms (e.g., UNION SELECT, ../) — those should block
            const terms = extractForbiddenTerms(input, res.foundPatterns);

            if (expandedChars.length === 0 && terms.length === 0) {
                return { blocked: false, details: res };
            }

            setState({
                open: true,
                chars: expandedChars,
                terms,
                patterns: [],
                field: fieldLabel,
                value: input
            });
            return { blocked: true, details: res };
        },
        [sanitize, expandCharsWithPatternHints, extractForbiddenTerms, CRITICAL_CHARS]
    );

    // ---------- Ready-to-render dialog ----------
    const WarningDialog = useCallback(
        () => (
            <SanitizationWarningDialog
                open={state.open}
                onClose={close}
                foundChars={state.chars}
                foundTerms={state.terms}
                foundPatterns={state.patterns} // hidden by your dialog anyway
                title="Unsafe Input Detected"
                message={
                    state.field
                        ? `Please make sure your ${state.field} does not contain the following:`
                        : "Please make sure your text does not contain the following:"
                }
            />
        ),
        [state, close]
    );

    return { check, checkFreeText, WarningDialog, close, state };
}
